import { SearchUnifiedListingModel } from "../../../interfaces/models/SearchUnifiedListingModel";
import { SearchFilters as StateSearchFilters } from "../../../interfaces/state/searchQuery";
import { BoundingBoxInterface } from "../types";
import { DEFAULT_ARRIVAL_DATE, DEFAULT_DEPARTURE_DATE } from "../constants/searchConstants";
import { 
    callChatNext, 
    createChatRequest, 
    createChatMessage, 
    createDeviceInfo, 
    createUserPreferences, 
    createClientContextualInfo,
    createSearchParameters,
    ChatNextRequest,
    SearchFilters as ChatSearchFilters
} from "../../../utils/api/chatApi";
import { getSessionIdFromUrl, updateUrlWithSession } from "../utils/sessionUtils";
import { setMessageUserMapping } from "../utils/messageIdMap";
import { getUserCurrencyLabel } from "../../../utils/userCurrencyUtils";

export interface SearchRequest {
    place_id?: string;
    startDate: string;
    endDate: string;
    searchFilters: StateSearchFilters;
    page?: number;
    boundingBox?: BoundingBoxInterface | null;
    text?: string;
    userModel?: any; // User model for currency detection
    onStreamingText?: (text: string) => void; // Callback for streaming text updates
    // Guest information
    adults?: number;
    children?: number;
    infants?: number;
    pets?: number;
    parentMessageId?: string; // Prefer running under this message id when provided
    isEdit?: boolean; // Use edit only for retroactive changes to an existing message id
}

export interface SearchResponse {
    listings: SearchUnifiedListingModel[];
    total: number;
    pageCount: number;
    distribution?: { [key: string]: number };
    assistantResponse?: string; // Store the final assistant response
    mapBoundary?: {
        bounds: {
            northeast: { lat: number; lng: number };
            southwest: { lat: number; lng: number };
        };
        center: { lat: number; lng: number };
        zoom: number;
    };
    // Optional correlation id for clients to drop stale results
    requestId?: string;
}

export interface SearchHistoryEntry {
    conversationId?: string;
    messageId?: string;
    userMessageId?: string;
    query: string;
    results: { [key: number]: SearchUnifiedListingModel[] };
    totalResults: number;
    pageCount: number;
    timestamp: number;
    responseData?: {
        listings: SearchUnifiedListingModel[];
        total: number;
        distribution?: { [key: string]: number };
        mapBoundary?: {
            bounds: {
                northeast: { lat: number; lng: number };
                southwest: { lat: number; lng: number };
            };
            center: { lat: number; lng: number };
            zoom: number;
        };
    };
    listingIds: string[];
    responseText: string;
    arrivalDate?: string;
    departureDate?: string;
    guests: {
        adults: number;
        children: number;
        infants: number;
        pets: number;
    };
    filters: StateSearchFilters;
    boundingBox?: BoundingBoxInterface;
}

class SearchService {
    private conversationId: string;
    private currentParentMessageId: string | null = null;
    // Track the most recent assistant message id for correlation with history entries
    private lastResponseMessageId: string | null = null;
    // Track the paired user (parent) message id for the current assistant message
    private currentUserMessageId: string | null = null;
    
    constructor() {
        // Initialize with session or URL if available; remote fetch handled lazily
        this.conversationId = this.getExistingConversationId();
    }
    
    /**
     * Get conversation ID from URL or create a new one
     */
    private getExistingConversationId(): string {
        // Prefer sessionStorage
        try {
            const stored = window.sessionStorage.getItem('search_conversation_id');
            if (stored) {
                return stored;
            }
        } catch {}
        // Fallback: from URL path
        const fromUrl = getSessionIdFromUrl();
        if (fromUrl) {
            try { window.sessionStorage.setItem('search_conversation_id', fromUrl); } catch {}
            return fromUrl;
        }
        return '';
    }

    // ensureConversationId removed: page guarantees id via URL/redirect

    /**
     * Fetch a stored search session by id from backend and normalize to SearchHistoryEntry[]
     * This should be used when a session id exists in the URL but no local/sessionStorage history is available.
     */
    async fetchStoredSession(sessionId: string): Promise<SearchHistoryEntry[] | null> {
        if (!sessionId) return null;
        try {
            // Attempt to fetch stored session. Backend returns session payload for the given id.
            const url = `https://search.anystay.com/conversation/${encodeURIComponent(sessionId)}`;
            const res = await fetch(url, {
                method: 'GET',
                headers: { 'Accept': 'application/json' }
            });
            if (!res.ok) {
                return null;
            }
            const payload: any = await res.json();
            const session = payload?.data || payload;
            const messages: any[] = Array.isArray(session?.messages) ? session.messages : [];
            if (messages.length === 0) return [];

            // Build history entries from assistant messages that include search_results
            const normalized: SearchHistoryEntry[] = [];
            for (let i = 0; i < messages.length; i++) {
                const msg = messages[i];
                if (!msg || msg.role !== 'assistant') continue;
                const meta = msg.metadata || {};
                const sr = meta.search_results || {};
                const results = Array.isArray(sr.results) ? sr.results : [];

                // Map backend results to SearchUnifiedListingModel used by UI
                const mapResultToUnified = (result: any): SearchUnifiedListingModel => {
                    const location = result.location || {};
                    const coords = result.coordinates || location.coordinates || undefined;
                    const rating = result.rating || {};
                    const price = result.price || {};
                    return {
                        id: result.id,
                        propertyTitle: result.propertyTitle || result.title || '',
                        propertyType: result.propertyType || result.type || 'apartment',
                        typeOfPlace: result.type || result.propertyType || 'apartment',
                        images: result.images || [],
                        owner: '',
                        hostProfilePhoto: undefined,
                        amenities: result.amenities || [],
                        rating: rating.overall !== undefined ? rating.overall : rating || undefined,
                        city: location.city || '',
                        state: location.state || '',
                        country: location.country || '',
                        suburb: location.suburb || '',
                        coordinates: coords ? { lat: coords.lat, lng: coords.lng } : undefined,
                        minimumPrice: result.minimumPrice ?? price.base ?? price.total ?? 0,
                        totalPrice: result.totalPrice ?? price.total ?? 0,
                        currency: result.currency ?? price.currency ?? 'USD',
                        guests: (result.guests && (result.guests.max ?? 0)) ?? 0,
                        beds: (result.guests && (result.guests.bedrooms ?? 0)) ?? 0,
                        overallAverage: rating.overall ?? undefined,
                        totalReviews: result.totalReviews,
                    } as SearchUnifiedListingModel;
                };
                const listings: SearchUnifiedListingModel[] = (results as any[]).map(mapResultToUnified);
                const listingIds = listings.map(l => l.id).filter(Boolean);
                const pagination = sr.pagination || {};
                const page = typeof pagination.page === 'number' ? pagination.page : 1;
                const total = typeof pagination.total === 'number' ? pagination.total : listings.length;
                const pageCount = typeof pagination.totalPages === 'number' ? pagination.totalPages : 1;
                const mapBoundary = sr.mapBoundary ? {
                    bounds: {
                        northeast: {
                            lat: sr.mapBoundary.bounds?.northeast?.lat,
                            lng: sr.mapBoundary.bounds?.northeast?.lng
                        },
                        southwest: {
                            lat: sr.mapBoundary.bounds?.southwest?.lat,
                            lng: sr.mapBoundary.bounds?.southwest?.lng
                        }
                    },
                    center: {
                        lat: sr.mapBoundary.center?.lat,
                        lng: sr.mapBoundary.center?.lng
                    },
                    zoom: sr.mapBoundary.zoom
                } : undefined;

                // Find nearest preceding user message for the query text and id
                let queryText = '';
                let userMessageId: string | undefined = undefined;
                for (let j = i - 1; j >= 0; j--) {
                    const prev = messages[j];
                    if (prev?.role === 'user' && typeof prev?.content === 'string') {
                        queryText = prev.content;
                        if (prev?.id) userMessageId = String(prev.id);
                        break;
                    }
                }
                if ((queryText || '').trim() === '') {
                    continue; // skip entries with empty query
                }

                const enhanced = sr.enhancedInput || {};
                const guests = meta.guests || enhanced.guests || { adults: 1, children: 0, infants: 0, pets: 0 };
                const dates = meta.dates || enhanced.dates || {};
                const filters = enhanced.filters || meta.filters || {};

                normalized.push({
                    conversationId: sessionId,
                    messageId: (msg.id || '').toString(),
                    userMessageId,
                    query: queryText,
                    results: { [page]: listings },
                    totalResults: total,
                    pageCount: pageCount,
                    timestamp: typeof msg.timestamp === 'number' ? msg.timestamp : Date.now(),
                    responseData: {
                        listings: listings,
                        total: total,
                        distribution: undefined,
                        mapBoundary
                    },
                    listingIds,
                    responseText: typeof msg.content === 'string' ? msg.content : '',
                    arrivalDate: dates.checkIn,
                    departureDate: dates.checkOut,
                    guests,
                    filters
                });
            }

            return normalized;
        } catch {
            return null;
        }
    }
    
    /**
     * Get current conversation ID
     */
    getConversationId(): string {
        return this.conversationId;
    }

    /**
     * Get the most recent streamed message id (used to key map meta)
     */
    getCurrentMessageId(): string | null {
        return this.currentParentMessageId;
    }

    /**
     * Get the most recent user (parent) message id
     */
    getCurrentUserMessageId(): string | null {
        return this.currentUserMessageId;
    }
    
    /**
     * Update conversation ID (when navigating to new session)
     */
    setConversationId(newId: string): void {
        // If switching to a different conversation, clear any cached message context
        if (this.conversationId !== newId) {
            this.currentParentMessageId = null;
            this.lastResponseMessageId = null;
            this.currentUserMessageId = null;
        }
        this.conversationId = newId;
        try { window.sessionStorage.setItem('search_conversation_id', newId); } catch {}
    }
    
    /**
     * Create filters object with only user-selected filters, or undefined if no filters selected
     */
    private createUserSelectedFilters(searchFilters: StateSearchFilters): ChatSearchFilters | undefined {
        // Check if user has selected any filters
        const hasSelectedFilters = (
            (searchFilters.propertyType && searchFilters.propertyType.length > 0) ||
            (searchFilters.amenities && searchFilters.amenities.length > 0) ||
            (searchFilters.typeOfPlace && searchFilters.typeOfPlace.length > 0) ||
            (searchFilters.optionsForYourStay && searchFilters.optionsForYourStay.length > 0) ||
            (searchFilters.houseRules && searchFilters.houseRules.length > 0) ||
            (searchFilters.bedrooms && searchFilters.bedrooms > 0) ||
            (searchFilters.beds && searchFilters.beds > 0) ||
            (searchFilters.bathrooms && searchFilters.bathrooms > 0) ||
            (searchFilters.priceRange && ((searchFilters.priceRange.min ?? 0) > 0 || (searchFilters.priceRange.max ?? 0) > 0))
        );

        // If no filters selected, return undefined
        if (!hasSelectedFilters) {
            return undefined;
        }

        // Return filters with only user-selected values
        return {
            typeOfPlace: (searchFilters.typeOfPlace && searchFilters.typeOfPlace.length > 0) 
                ? searchFilters.typeOfPlace 
                : [],
            propertyType: (searchFilters.propertyType && searchFilters.propertyType.length > 0) 
                ? searchFilters.propertyType 
                : [],
            optionsForYourStay: (searchFilters.optionsForYourStay && searchFilters.optionsForYourStay.length > 0) 
                ? searchFilters.optionsForYourStay 
                : [],
            priceRange: searchFilters.priceRange || { min: 0, max: 0 },
            bedrooms: searchFilters.bedrooms || 0,
            beds: searchFilters.beds || 0,
            bathrooms: searchFilters.bathrooms || 0,
            amenities: (searchFilters.amenities && searchFilters.amenities.length > 0) 
                ? searchFilters.amenities 
                : [],
            houseRules: (searchFilters.houseRules && searchFilters.houseRules.length > 0) 
                ? searchFilters.houseRules 
                : []
        };
    }

    /**
     * Convert old search parameters to new chat API format
     */
    private convertToChatApiRequest(request: SearchRequest): ChatNextRequest {
        const {
            place_id,
            startDate,
            endDate,
            searchFilters,
            page = 1,
            boundingBox,
            text = "",
            userModel,
            adults = 1,
            children = 0,
            infants = 0,
            pets = 0,
            parentMessageId,
            isEdit
        } = request;

        // Enforce explicit parent id for edits
        if (isEdit && !parentMessageId) {
            throw new Error("Edit requested without parentMessageId");
        }

        // trimmed chat request logging

        // Create device info
        const device = createDeviceInfo('desktop', navigator.userAgent);

        // Do not include transient search filters in user preferences
        const preferences = createUserPreferences(
            [],
            [],
            { min: 0, max: 0 }
        );

        // Create client context
        const clientContext = createClientContextualInfo(
            userModel?.id || userModel?.identityID || 'search_user', // Use actual user ID if available
            device,
            preferences
        );

        // Convert dates - default to tonight and tomorrow if not specified
        const getDefaultDates = () => {
            const tonight = new Date();
            tonight.setHours(15, 0, 0, 0); // 3 PM today
            
            const tomorrow = new Date(tonight);
            tomorrow.setDate(tomorrow.getDate() + 1);
            tomorrow.setHours(11, 0, 0, 0); // 11 AM tomorrow
            
            return {
                checkIn: tonight.toISOString().split('T')[0], // YYYY-MM-DD format
                checkOut: tomorrow.toISOString().split('T')[0]
            };
        };

        const defaultDates = getDefaultDates();
        const checkIn = startDate === "anyArrival" ? defaultDates.checkIn : startDate;
        const checkOut = endDate === "anyDeparture" ? defaultDates.checkOut : endDate;

        // Create filtered search parameters - only include filters that user has actually selected
        const userSelectedFilters = this.createUserSelectedFilters(searchFilters);
        
        // Create search parameters
        const searchParams = createSearchParameters(
            undefined, // Let backend determine location from text
            boundingBox ? (boundingBox.topRight.lat + boundingBox.bottomLeft.lat) / 2 : undefined,
            boundingBox ? (boundingBox.topRight.lon + boundingBox.bottomLeft.lon) / 2 : undefined,
            { checkIn, checkOut },
            {
                adults: adults, // Default to 1 adult
                children: children,
                infants: infants,
                pets: pets
            },
            getUserCurrencyLabel(userModel), // Use user's preferred currency
            { page, limit: 20 },
            userSelectedFilters // Only include filters if user has selected any
        );

        // Create messages
        const messages = [
            createChatMessage('user', text)
        ];

        // Determine effective parent id: never fall back to latest message implicitly
        const effectiveParentId = parentMessageId || undefined;
        const hasParent = !!effectiveParentId;
        // Only use 'edit' when explicitly requested AND an explicit parent id is provided
        const action: 'next' | 'edit' = (isEdit && hasParent) ? 'edit' : 'next';
        const chatRequest = createChatRequest(this.conversationId, messages, {
            action,
            ...(hasParent ? { parentMessageId: effectiveParentId } : {}),
            clientContextualInfo: clientContext,
            searchParameters: searchParams
        });
        return chatRequest;
    }

    /**
     * Parse server-sent events response to extract search results and stream assistant response
     */
    private async parseSSEResponse(
        response: Response, 
        onStreamingText?: (text: string) => void,
        onError?: (error: { message: string; retryable: boolean; timestamp: number }) => void
    ): Promise<SearchResponse> {
        const reader = response.body?.getReader();
        if (!reader) {
            const error = {
                message: 'No response body received from server',
                retryable: true,
                timestamp: Date.now()
            };
            onError?.(error);
            throw new Error(error.message);
        }

        const decoder = new TextDecoder();
        let buffer = '';
        let searchResults: SearchResponse = {
            listings: [],
            total: 0,
            pageCount: 1,
            distribution: {},
            assistantResponse: ''
        };

        // Track streaming text for assistant response
        let streamingText = '';
        let isStreamingAssistantResponse = false;
        let hasReceivedData = false;

        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                hasReceivedData = true;
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || ''; // Keep incomplete line in buffer

                for (const line of lines) {
                    // Handle different event types
                    if (line.startsWith('event: ')) {
                        const eventType = line.slice(7).trim();
                        
                        // Check if we're starting to stream assistant response
                        if (eventType === 'delta') {
                            isStreamingAssistantResponse = true;
                        }
                        continue;
                    }

                    if (line.startsWith('data: ')) {
                        const data = line.slice(6);
                        if (data === '[DONE]') {
                            // Store final assistant response
                            searchResults.assistantResponse = streamingText;
                            // trimmed SSE done logging
                            return searchResults;
                        }

                        try {
                            const parsed = JSON.parse(data);
                            
                            // Handle different event types
                            if (parsed.type === 'search_results') {
                                // Extract search results from the new format
                                const results = parsed.results || [];
                                // trimmed SSE results logging
                                
                                // Convert new format to SearchUnifiedListingModel format
                                const listings: SearchUnifiedListingModel[] = results.map((result: any) => ({
                                    id: result.id,
                                    propertyTitle: result.title || '',
                                    propertyType: result.propertyType || result.type || 'apartment',
                                    typeOfPlace: result.type || 'apartment',
                                    images: result.images || [],
                                    owner: '', // Not provided in new format
                                    hostProfilePhoto: undefined, // Not provided in new format
                                    amenities: result.amenities || [],
                                    rating: result.rating || undefined,
                                    city: result.location?.city || '',
                                    state: result.location?.state || '',
                                    country: result.location?.country || '',
                                    suburb: result.location?.suburb || '',
                                    coordinates: result.location?.coordinates ? {
                                        lat: result.location.coordinates.lat,
                                        lng: result.location.coordinates.lng
                                    } : undefined,
                                    minimumPrice: result.price?.total || result.price?.base || 0,
                                    totalPrice: result.price?.total || 0,
                                    currency: result.price?.currency || 'USD',
                                    guests: result.guests?.max || 0,
                                    beds: result.guests?.bedrooms || 0,
                                    overallAverage: result.rating?.overall || undefined,
                                    totalReviews: undefined // Not provided in new format
                                }));

                                searchResults.listings = listings;
                                searchResults.total = parsed.pagination?.total || results.length;
                                searchResults.pageCount = parsed.pagination?.totalPages || 1;
                                
                                // Extract map boundary if available
                                if (parsed.map_boundary) {
                                    searchResults.mapBoundary = {
                                        bounds: {
                                            northeast: {
                                                lat: parsed.map_boundary.bounds.northeast.lat,
                                                lng: parsed.map_boundary.bounds.northeast.lng
                                            },
                                            southwest: {
                                                lat: parsed.map_boundary.bounds.southwest.lat,
                                                lng: parsed.map_boundary.bounds.southwest.lng
                                            }
                                        },
                                        center: {
                                            lat: parsed.map_boundary.center.lat,
                                            lng: parsed.map_boundary.center.lng
                                        },
                                        zoom: parsed.map_boundary.zoom
                                    };
                                }
                                
                                // Prefer backend-provided price distribution when present
                                if (parsed.distribution && typeof parsed.distribution === 'object') {
                                    // Expect shape: { "0": n, ..., "59": n, min: number, max: number, total: number }
                                    searchResults.distribution = parsed.distribution as { [key: string]: number };
                                } else {
                                    // Fallback: construct a simple distribution from amenities (legacy behavior)
                                    const distribution: { [key: string]: number } = {};
                                    results.forEach((result: any) => {
                                        if (result.amenities) {
                                            result.amenities.forEach((amenity: string) => {
                                                distribution[amenity] = (distribution[amenity] || 0) + 1;
                                            });
                                        }
                                    });
                                    searchResults.distribution = distribution;
                                }
                            }
                            
                            // Handle completion events
                            else if (parsed.type === 'message_stream_complete') {
                                // Stream is complete, store the final assistant response
                                isStreamingAssistantResponse = false;
                                searchResults.assistantResponse = streamingText;
                                // Capture assistant message id on completion when provided
                                if (parsed.message_id) {
                                    this.lastResponseMessageId = parsed.message_id;
                                    this.currentParentMessageId = parsed.message_id;
                                    const parentFromMsg = (parsed as any).parent_message_id;
                                    const parentFromMeta = (parsed as any).metadata?.parent_id;
                                    const parentId = parentFromMsg || parentFromMeta;
                                    if (parentId) { 
                                        try { 
                                            this.currentUserMessageId = String(parentId); 
                                            setMessageUserMapping(getSessionIdFromUrl(), this.lastResponseMessageId, this.currentUserMessageId);
                                        } catch {}
                                    }
                                }
                                continue;
                            }
                            
                            // Handle conversation metadata
                            else if (parsed.type === 'conversation_detail_metadata') {
                                // Metadata received, continue processing
                                continue;
                            }
                            
                            // Handle resume token
                            else if (parsed.type === 'resume_conversation_token') {
                                // Resume token received, continue processing
                                continue;
                            }
                            
                            
                            // Handle delta events (assistant token stream)
                            else if (parsed.v !== undefined) {
                                // This is a delta event for assistant token streaming (v1/v2)
                                if (isStreamingAssistantResponse && typeof parsed.v === 'string') {
                                    streamingText += parsed.v;
                                    
                                    // Call the streaming callback if provided
                                    if (onStreamingText) {
                                        onStreamingText(streamingText);
                                    }
                                }
                                // Capture assistant message id from delta payload if present
                                try {
                                    const vAny: any = (parsed as any).v;
                                    const msg = vAny?.message;
                                    const mid = msg?.id;
                                    const role = msg?.author?.role;
                                    if (mid && (!role || role === 'assistant')) {
                                        this.lastResponseMessageId = mid;
                                    }
                                    const parentId = msg?.parent_message_id
                                        || vAny?.metadata?.parent_id
                                        || msg?.metadata?.parent_id
                                        || (parsed as any).parent_message_id
                                        || (parsed as any).metadata?.parent_id;
                                    if (parentId) {
                                        this.currentUserMessageId = String(parentId);
                                        try { setMessageUserMapping(getSessionIdFromUrl(), this.lastResponseMessageId, this.currentUserMessageId); } catch {}
                                    }
                                } catch {}
                                continue;
                            }
                            
                            // Handle delta_encoding events (new format)
                            else if (parsed.type === 'delta_encoding') {
                                // Delta encoding header - indicates v1 format
                                continue;
                            }
                            
                            // Handle message markers for streaming start
                            else if (parsed.type === 'message_marker') {
                                if (parsed.marker === 'user_visible_token' && parsed.event === 'first') {
                                    isStreamingAssistantResponse = true;
                                    streamingText = '';
                                    if (parsed.message_id) {
                                        this.lastResponseMessageId = parsed.message_id;
                                        this.currentParentMessageId = parsed.message_id;
                                        // Capture parent (user) id when provided by stream
                                        const parentFromMsg = (parsed as any).parent_message_id;
                                        const parentFromMeta = (parsed as any).metadata?.parent_id;
                                        const parentId = parentFromMsg || parentFromMeta;
                                        if (parentId) { 
                                            try { 
                                                this.currentUserMessageId = String(parentId); 
                                                setMessageUserMapping(getSessionIdFromUrl(), this.lastResponseMessageId, this.currentUserMessageId);
                                            } catch {}
                                        }
                                    }
                                }
                                continue;
                            }
                            
                            // Store message/parent ID if available in any event
                            if ((parsed as any).message_id) {
                                try {
                                    const mid = (parsed as any).message_id;
                                    this.lastResponseMessageId = mid;
                                    this.currentParentMessageId = mid;
                                } catch {}
                            }
                            const parentFallback = (parsed as any).parent_message_id || (parsed as any).metadata?.parent_id;
                            if (parentFallback && !this.currentUserMessageId) {
                                try { 
                                    this.currentUserMessageId = String(parentFallback); 
                                    setMessageUserMapping(getSessionIdFromUrl(), this.lastResponseMessageId, this.currentUserMessageId);
                                } catch {}
                            }
                            
                        } catch (e) {
                            
                        }
                    }
                }
            }
        } catch (error) {
            const streamingError = {
                message: error instanceof Error ? error.message : 'Unknown streaming error',
                retryable: true,
                timestamp: Date.now()
            };
            onError?.(streamingError);
            throw error;
        } finally {
            reader.releaseLock();
        }

        // If we haven't received any data, it might be a connection issue
        if (!hasReceivedData) {
            const error = {
                message: 'No data received from server. Please check your connection.',
                retryable: true,
                timestamp: Date.now()
            };
            onError?.(error);
            throw new Error(error.message);
        }

        return searchResults;
    }

    /**
     * Fetch search listings using the chat API
     */
    async fetchListings(
        request: SearchRequest,
        onError?: (error: { message: string; retryable: boolean; timestamp: number }) => void,
        signal?: AbortSignal
    ): Promise<SearchResponse> {
        try {
            // Convert to chat API format
            const chatRequest = this.convertToChatApiRequest(request);
            
            // Make the API call
            const response = await callChatNext(chatRequest, signal);
            
            // Check if response is ok
            if (!response.ok) {
                const error = {
                    message: `Server error: ${response.status} ${response.statusText}`,
                    retryable: response.status >= 500, // Retry on server errors
                    timestamp: Date.now()
                };
                onError?.(error);
                throw new Error(error.message);
            }
            
            // Parse the server-sent events response with streaming callback
            const searchResults = await this.parseSSEResponse(response, request.onStreamingText, onError);
            
            return searchResults;
        } catch (error) {
            // Handle network and other errors
            const streamingError = {
                message: error instanceof Error ? error.message : 'Unknown error occurred',
                retryable: true,
                timestamp: Date.now()
            };
            onError?.(streamingError);
            throw error;
        }
    }

    /**
     * Create a search history entry from search results
     */
    createSearchHistoryEntry(
        searchResults: SearchResponse,
        searchRequest: SearchRequest,
        page: number,
        text: string
    ): SearchHistoryEntry {
        const listingIds = searchResults.listings?.map((listing: SearchUnifiedListingModel) => listing.id) || [];
        
        return {
            conversationId: this.conversationId || undefined,
            messageId: this.lastResponseMessageId || this.currentParentMessageId || undefined,
            // Prefer explicit parent from the request (edits), fallback to stream-captured parent
            userMessageId: searchRequest.parentMessageId || this.currentUserMessageId || undefined,
            query: text || "",
            results: { [page]: [...searchResults.listings] },
            totalResults: searchResults.total,
            pageCount: searchResults.pageCount,
            timestamp: Date.now(),
            responseData: {
                listings: [...searchResults.listings],
                total: searchResults.total,
                distribution: searchResults.distribution,
                mapBoundary: searchResults.mapBoundary,
            },
            listingIds: listingIds,
            responseText: searchResults.assistantResponse || "",
            arrivalDate: searchRequest.startDate !== DEFAULT_ARRIVAL_DATE ? searchRequest.startDate : undefined,
            departureDate: searchRequest.endDate !== DEFAULT_DEPARTURE_DATE ? searchRequest.endDate : undefined,
            guests: {
                adults: searchRequest.adults || 1,
                children: searchRequest.children || 0,
                infants: searchRequest.infants || 0,
                pets: searchRequest.pets || 0,
            },
            boundingBox: searchRequest.boundingBox || undefined,
            filters: searchRequest.searchFilters
        };
    }

    /**
     * Update search history with a new entry
     */
    updateSearchHistory(
        currentHistory: SearchHistoryEntry[],
        newEntry: SearchHistoryEntry,
        maxEntries: number = 50
    ): SearchHistoryEntry[] {
        const newHistory = [...currentHistory, newEntry];
        return newHistory.slice(-maxEntries); // Keep last N entries
    }
}

// Export a singleton instance
export const searchService = new SearchService();
